import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, useFlashMessage } from '@indigo-ag/component-module';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { faPaperPlane, faThumbsUp, faThumbsDown, faPauseCircle, faPlus } from '@fortawesome/pro-regular-svg-icons';

import i18next from 'core/i18n';

// TODO: should this component be on shared?
import { DecisionModal } from 'features/brazil-grower/components/BrazilGrowerFinance/shared/DecisionModal/DecisionModal';
import CreditExceptionsForm, {
  CreditExceptionsFormHandle
} from 'features/brazil-grower/components/BrazilGrowerFinance/components/CreditExceptions/CreditExceptionsForm';
import styled from 'styled-components';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';

const CustomCreditExceptionsForm = styled(CreditExceptionsForm)`
  padding: 0px;
`;

export enum DecisionOptions {
  VALIDATE = 'VALIDATE',
  SEND_TO_CREDIT_TEAM = 'SEND_TO_CREDIT_TEAM',
  APPROVE = 'APPROVE',
  DENY = 'DENY',
  PUT_ON_HOLD = 'PUT_ON_HOLD',
  SEND_TO_COMMITTEE = 'SEND_TO_COMMITTEE',
  APPROVE_AMENDMENT_CREATION = 'APPROVE_AMENDMENT_CREATION',
  CREATE_AMENDMENT_WITHOUT_APPROVAL = 'CREATE_AMENDMENT_WITHOUT_APPROVAL'
}

const decisionTitles: { [key in DecisionOptions]: string } = {
  [DecisionOptions.VALIDATE]: i18next.t('validating'),
  [DecisionOptions.SEND_TO_CREDIT_TEAM]: i18next.t('sendingToCreditTeam'),
  [DecisionOptions.APPROVE]: i18next.t('approving'),
  [DecisionOptions.DENY]: i18next.t('denying'),
  [DecisionOptions.PUT_ON_HOLD]: `${i18next.t('onHold')} ${i18next.t('phase')} 3`,
  [DecisionOptions.SEND_TO_COMMITTEE]: i18next.t('sendingToCommittee'),
  [DecisionOptions.APPROVE_AMENDMENT_CREATION]: i18next.t('approvingAmendmentCreation'),
  [DecisionOptions.CREATE_AMENDMENT_WITHOUT_APPROVAL]: i18next.t('creatingAmendmentWithoutApproval')
};

const decisionButtonTexts: { [key in DecisionOptions]: string } = {
  [DecisionOptions.VALIDATE]: i18next.t('validate'),
  [DecisionOptions.SEND_TO_CREDIT_TEAM]: i18next.t('sendToCreditTeam'),
  [DecisionOptions.APPROVE]: i18next.t('approve'),
  [DecisionOptions.DENY]: i18next.t('deny'),
  [DecisionOptions.PUT_ON_HOLD]: i18next.t('onHold'),
  [DecisionOptions.SEND_TO_COMMITTEE]: i18next.t('sendToCommittee'),
  [DecisionOptions.APPROVE_AMENDMENT_CREATION]: i18next.t('approveAmendmentCreation'),
  [DecisionOptions.CREATE_AMENDMENT_WITHOUT_APPROVAL]: i18next.t('createAmendmentWithoutApproval')
};

const decisionButtonIcons: { [key in DecisionOptions]: IconDefinition } = {
  [DecisionOptions.VALIDATE]: faThumbsUp,
  [DecisionOptions.SEND_TO_CREDIT_TEAM]: faPaperPlane,
  [DecisionOptions.APPROVE]: faThumbsUp,
  [DecisionOptions.DENY]: faThumbsDown,
  [DecisionOptions.PUT_ON_HOLD]: faPauseCircle,
  [DecisionOptions.SEND_TO_COMMITTEE]: faPaperPlane,
  [DecisionOptions.APPROVE_AMENDMENT_CREATION]: faPlus,
  [DecisionOptions.CREATE_AMENDMENT_WITHOUT_APPROVAL]: faPlus
};

export type CreditExceptionParams = {
  roundId: string;
  creditExceptions: string[];
};

export type DocumentsValidationAvailableDecisions = {
  [key in DecisionOptions]?: {
    descriptionMessage: string;
    successMessage: string;
    onConfirmAction: (notes: string, creditExceptionParams?: CreditExceptionParams) => Promise<any>;
  };
};

interface DocumentsValidationDecisionProps {
  availableDecisions: DocumentsValidationAvailableDecisions;
}

export const DocumentsValidationDecision: React.FunctionComponent<DocumentsValidationDecisionProps> = ({
  availableDecisions
}) => {
  const { creditApprovalDataQuery, importedExceptions } = useContext(FinanceProcessContext);
  const round = creditApprovalDataQuery?.data?.creditApprovalData?.round;
  const initialExceptions = round?.creditExceptions ?? [];
  /**
   * State
   */
  const [isDecisionOpen, setIsDecisionOpen] = useState(false);
  const [decision, setDecision] = useState<DecisionOptions>();
  const [newExceptions, setNewExceptions] = useState<string[]>(round?.creditExceptions ?? []);
  const [inputValue, setInputValue] = useState('');

  const formRef = useRef<CreditExceptionsFormHandle>(null);

  const { addFlashMessage } = useFlashMessage();

  useEffect(() => {
    setNewExceptions(round?.creditExceptions ?? []);
  }, [round, isDecisionOpen]);

  const isCreditExceptionsValid = () => {
    if (decision === DecisionOptions.SEND_TO_COMMITTEE && round) {
      return !(formRef.current?.hasOtherExceptionSelected() && !formRef.current?.validateOtherException(inputValue));
    }

    return true;
  };

  const buildCreditExceptionParams = (): CreditExceptionParams | undefined => {
    const newExceptionsCopy = newExceptions?.slice() ?? [];
    const haveExceptionsChanged =
      JSON.stringify(initialExceptions.slice().sort()) !== JSON.stringify(newExceptionsCopy.sort());

    return decision === DecisionOptions.SEND_TO_COMMITTEE && round && haveExceptionsChanged
      ? {
          roundId: round.id,
          creditExceptions: newExceptions
        }
      : undefined;
  };

  const confirmDecision = async (decisionNotes: string) => {
    if (decision && !!availableDecisions[decision]) {
      const creditExceptionParams = buildCreditExceptionParams();

      if (!isCreditExceptionsValid()) return;

      try {
        await availableDecisions[decision]!.onConfirmAction(decisionNotes, creditExceptionParams);

        setDecision(undefined);
        setIsDecisionOpen(false);

        addFlashMessage({
          content: availableDecisions[decision]!.successMessage,
          title: '',
          variant: 'success'
        });

        closeDecisionModal();
      } catch (error) {
        addFlashMessage({
          content: i18next.t('error'),
          title: '',
          variant: 'error'
        });
      }
    }
  };

  const selectDecision = (d: DecisionOptions) => {
    return () => {
      setDecision(d);
      setIsDecisionOpen(true);
    };
  };

  const closeDecisionModal = () => {
    setIsDecisionOpen(false);
    setDecision(undefined);
    setInputValue('');
  };

  const extraContent = () =>
    decision === DecisionOptions.SEND_TO_COMMITTEE ? (
      <CustomCreditExceptionsForm
        ref={formRef}
        newExceptions={newExceptions}
        setNewExceptions={setNewExceptions}
        inputValue={inputValue}
        setInputValue={setInputValue}
        alreadyAdded={importedExceptions}
      />
    ) : null;

  return (
    <div className="flex justify-flex-end mt-4 mb-4" style={{ flexWrap: 'wrap' }}>
      {Object.keys(availableDecisions).map(d => (
        <Button
          key={d}
          variant="secondary"
          size="small"
          className="text-transform-capitalize"
          faIconAfter={decisionButtonIcons[d as DecisionOptions]}
          onClick={selectDecision(d as DecisionOptions)}
        >
          {decisionButtonTexts[d as DecisionOptions]}
        </Button>
      ))}
      <DecisionModal
        open={isDecisionOpen}
        title={(decision && decisionTitles[decision]) || ''}
        message={(decision && availableDecisions[decision]?.descriptionMessage) || ''}
        closeModal={closeDecisionModal}
        onConfirmAction={confirmDecision}
        extraContent={extraContent()}
      />
    </div>
  );
};
